import { isValid as isDateTimeValid, format } from "date-fns";
import appConstants from "../../../../../../appConstants";
import { postData } from "../../../../../../api/componentAPIs";

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString || !isDateTimeValid(dateTimeString)) {
    return "";
  }
  const date = format(dateTimeString, appConstants.DATETYPES.MONTHDATEYEAR.format);
  const time = format(dateTimeString, appConstants.DATETYPES.TIME.format);
  return `${date} | ${time}`;
};

const formatTime = (dateTimeString) => {
   if (!dateTimeString || !isDateTimeValid(dateTimeString)) {
     return "";
   }
   return format(dateTimeString, appConstants.DATETYPES.TIME.format);
}

const getPickupShipments = (pickupId, shipmentData) => {
  const pickup = shipmentData.find(
    (pickup) => pickup.pickupId.toUpperCase() === pickupId.toUpperCase()
  );
  return pickup ? pickup.shipments : [];
};

/**
 * Active `GetPickupShipments` with `pickupIds`: response is `[{ pickupId, shipments }, ...]`.
 * Flattens to one row list for the grid and ensures `pickupNumber` is set when the API omits it on nested rows.
 */
const buildFlattenedShipmentsFromBatchPickupResponse = (shipmentData, groupMemberPickups) => {
  if (!Array.isArray(shipmentData) || shipmentData.length === 0) {
    return [];
  }
  const pickupNumberByPickupId = new Map(
    (groupMemberPickups || []).map((p) => [String(p.pickupId || '').toUpperCase(), p.pickupNumber])
  );
  const first = shipmentData[0];
  if (first && Array.isArray(first.shipments)) {
    return shipmentData.flatMap((entry) =>
      (entry.shipments || []).map((shipment) => {
        const pickupIdKey = String(entry.pickupId || '').toUpperCase();
        const pickupNumberFromMemberList = pickupNumberByPickupId.get(pickupIdKey);
        return {
          ...shipment,
          pickupNumber: shipment.pickupNumber ?? pickupNumberFromMemberList
        };
      })
    );
  }
  return [];
};

/** Maps API shipment payload to grid rows for the pickup info dialog (past vs active shapes). */
const resolveShipmentRowsFromPickupShipmentsResponse = ({
  responseData,
  pickupId,
  groupMemberPickups,
  isPastPickup
}) => {
  if (isPastPickup) {
    return responseData;
  }
  if (Array.isArray(responseData) && responseData[0]?.shipments) {
    return buildFlattenedShipmentsFromBatchPickupResponse(responseData, groupMemberPickups);
  }
  return getPickupShipments(pickupId, responseData);
};

const getPickupShipmentList = ({
  terminalCode,
  pickupId,
  pickupIds,
  groupMemberPickups,
  shipmentInfo,
  isPastPickup,
  pickupNumber,
  shipmentAPI,
  pastPickupShipments
}) => {
  if (!shipmentAPI) {
    return Promise.resolve({ data: pastPickupShipments ?? [], gridStatus: appConstants.GRID_STATUS.SUCCESS });
  }
  if (shipmentInfo.gridStatus !== appConstants.GRID_STATUS.LOADING) {
    return Promise.resolve({ data: [], gridStatus: appConstants.GRID_STATUS.LOADING });
  }
  let resolvedPickupIds = [];
  if (pickupIds?.filter(Boolean)?.length > 0) {
    resolvedPickupIds = pickupIds;
  } else if (pickupId) {
    resolvedPickupIds = [pickupId];
  }
  const params = isPastPickup
    ? {
        terminalCode,
        pickupNumber: pickupNumber
      }
    : {
        terminalCode,
        pickupIds: resolvedPickupIds
      };
  return postData(shipmentAPI, params).then(
    (response) => {
      if (response && response.status === 200) {
        const data = resolveShipmentRowsFromPickupShipmentsResponse({
          responseData: response.data,
          pickupId,
          groupMemberPickups,
          isPastPickup
        });
        return {
          data,
          gridStatus: appConstants.GRID_STATUS.SUCCESS
        };
      }
      return { data: [], gridStatus: appConstants.GRID_STATUS.ERROR };
    },
    (error) => {
      return { data: [], gridStatus: appConstants.GRID_STATUS.ERROR };
    }
  );
};

const getFormattedPickupToggleInfo = (pickupInfo) => {
  return [
    { label: 'Is Exhibit Pickup', value: pickupInfo.isExhibitPickup ? 'Yes' : '' },
    { label: 'Is Pickup a Trailer Swap', value: pickupInfo.isSwapPickup ? 'Yes' : '' },
    { label: 'Is Pickup a Dock Drop', value: pickupInfo.isDockDropPickup ? 'Yes' : '' },
    { label: 'Is Lift Gate Required', value: pickupInfo.isLiftGate ? 'Yes' : '' },
    { label: 'Is Warehouse Pickup', value: pickupInfo.isWarehousePickup ? 'Yes' : '' },
    { label: 'Is Daily Pickup', value: pickupInfo.isDailyPickup ? 'Yes' : '' }
  ].filter((item) => item.value);
};

const getFormattedPickupStatusToggleInfo = (pickupInfo) => {
  return [
    { label: 'Shipper Load and Count', value: pickupInfo.isShipperLoadAndCount ? 'Yes' : '' },
    {
      label: 'Is driver allowed inside to complete pickup?',
      value: pickupInfo.isDriverAllowedInside ? 'Yes' : '',
      showColon: false
    }
  ].filter((item) => item.value);
};

const isUnassignedNoFreightPickup = (isNoFreight, driverNumber) => {
  return !driverNumber && isNoFreight;
};

const shouldShowRequestorInPickupInfoPopup = ({
  enableRequestorDetailsForPickups,
  isGroupPickupDialog
}) => Boolean(enableRequestorDetailsForPickups) && !isGroupPickupDialog;

export {
  formatDateTime,
  formatTime,
  getPickupShipmentList,
  getFormattedPickupToggleInfo,
  getFormattedPickupStatusToggleInfo,
  isUnassignedNoFreightPickup,
  shouldShowRequestorInPickupInfoPopup,
};
